import { mq } from "@/constants/breakpoints";
import { blogAuthorPath } from "@/constants/paths";
import options from "@/utils/richTextOptions";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { css } from "@emotion/react";
import Head from "next/head";
import Link from "next/link";
import Container from "../Container/Container";
import Layout from "../Layout/Layout";
import { H3Css, H5Css, H8_16Css, H9Css, Heading } from "../Typography";
import BlogPageCategories from "./BlogPageCategories";
import BlogPageHeroImage from "./BlogPageHeroImage";
import BlogSignup from "./BlogSignup";
import SocialSharing from "./SocialSharing";
import TableOfContents from "./TableOfContents";

const ldJson = (
  slug: string,
  title: string,
  datePublished: string,
  dateModified: string,
  authorSlug: string,
  authorName: string,
  metaDescription: string,
  imageUrl: string
) => ({
  "@context": "http://schema.org",
  "@type": "BlogPosting",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `https://www.jerseywatch.com/blog/${slug}`,
  },
  headline: title,
  image: imageUrl,
  datePublished: datePublished,
  dateModified: dateModified,
  author: {
    "@type": `${
      authorSlug === "the-jersey-watch-team" ? "Organization" : "Person"
    }`,
    name: authorName,
  },
  publisher: {
    "@type": "Organization",
    name: "Jersey Watch",
  },
  description: metaDescription,
});

interface BlogPageProps {
  blogPost: any;
  isPreview?: boolean;
}

const BlogPage = ({ blogPost, isPreview }: BlogPageProps) => {
  const ldJsonData = JSON.stringify(
    ldJson(
      blogPost.slug,
      blogPost.title,
      blogPost.publishedDatetime,
      blogPost.sys.publishedAt,
      blogPost.author.slug,
      blogPost.author.name,
      blogPost.metadesc,
      blogPost.featuredMedia.url
    )
  );

  return (
    <Layout
      title={blogPost.title}
      metaDescription={blogPost.metadesc}
      imageUrl={blogPost.featuredMedia.url}
      heroContainerCss={css`
        background: url("/images/blog-post/BG.svg"),
          linear-gradient(112.24deg, #048cdd 4.51%, #32b2ff 100.81%);
        background-repeat: no-repeat;
        background-size: cover;
        ${mq["lg"]} {
          height: 540px;
        }
      `}
      hero={
        <Container>
          <Head>
            {isPreview && <meta name="robots" content="noindex, nofollow" />}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: ldJsonData }}
            />
          </Head>
          <div
            css={css`
              display: block;
              ${mq["lg"]} {
                display: flex;
                gap: 40px;
              }
            `}
          >
            <div
              css={css`
                flex: 1;
              `}
            >
              <div
                css={css`
                  margin: auto;
                  width: fit-content;
                  margin-top: 24px;
                  ${mq["lg"]} {
                    margin: unset;
                    margin-top: 42px;
                  }
                `}
              >
                <BlogPageCategories
                  categories={blogPost.categoriesNewCollection.items}
                />
              </div>
              <Heading
                level={1}
                css={css`
                  padding-top: 18px;
                  color: white;
                  text-align: center;
                  ${H5Css}
                  ${mq["lg"]} {
                    text-align: left;
                    padding-top: 24px;
                    ${H3Css}
                  }
                `}
              >
                {blogPost.title}
              </Heading>

              <Heading
                css={css`
                  margin-top: 14px;
                  color: #fff;
                  ${mq["lg"]} {
                    margin-top: 24px;
                    ${H8_16Css}
                  }
                `}
              >
                <div
                  css={css`
                    width: fit-content;
                    display: block;
                    margin: auto;
                    ${mq["lg"]} {
                      margin: unset;
                    }
                  `}
                >
                  <Link href={blogAuthorPath(blogPost.author.slug, 1)}>
                    <span
                      css={css`
                        opacity: 0.8;
                        transition: opacity 0.2s ease;
                        ${H9Css}
                        &:hover {
                          opacity: 1;
                        }
                      `}
                    >
                      {blogPost.author.name}{" "}
                    </span>
                  </Link>
                  <span
                    css={css`
                      opacity: 0.8;
                      font-size: 12px;
                      line-height: 14px;
                      margin-left: 14px;
                    `}
                  >
                    Last updated:{" "}
                    {new Date(
                      blogPost.sys.updatedAt || blogPost.sys.publishedAt
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
              </Heading>
              <div
                css={css`
                  position: relative;
                  margin: 37px auto 0;
                  text-align: center;
                  ${mq["lg"]} {
                    display: none;
                  }
                `}
              >
                <div
                  css={css`
                    max-width: 100%;
                  `}
                >
                  <BlogPageHeroImage featuredMedia={blogPost.featuredMedia} />
                </div>
              </div>
            </div>
            <div
              css={css`
                display: none;
                ${mq["lg"]} {
                  flex: 0.4;
                  margin-top: 30px;
                  display: block;
                  position: relative;
                }
              `}
            >
              <BlogPageHeroImage featuredMedia={blogPost.featuredMedia} />
            </div>
          </div>
        </Container>
      }
    >
      <Container
        key={blogPost.slug}
        css={css`
          padding-bottom: 60px;
          ${mq["lg"]} {
            display: ${blogPost.tableOfContents ? "flex" : "block"};
            margin-top: ${blogPost.tableOfContents ? "80px" : "unset"};
            gap: 55px;
            padding-bottom: 80px;
          }
          ${mq["xl"]} {
            justify-content: space-between;
            padding-left: 0;
            padding-right: 0;
            max-width: 1500px;
            gap: unset;
          }
        `}
      >
        {blogPost.tableOfContents ? (
          <TableOfContents blogPostJson={blogPost.bodyRich.json} />
        ) : (
          <div
            css={css`
              margin-top: 60px;
              ${mq["lg"]} {
                margin-top: 80px;
              }
            `}
          />
        )}
        <div
          id="blog-container"
          css={css`
            margin: ${blogPost.tableOfContents ? "unset" : "auto"};
            ${mq["lg"]} {
              max-width: 600px;
            }
            ${mq["xl"]} {
              flex: 0 0 600px;
            }
          `}
        >
          {documentToReactComponents(
            blogPost.bodyRich.json,
            options(blogPost.bodyRich.links)
          )}
        </div>
        <div
          css={css`
            display: none;
            ${mq["xl"]} {
              display: block;
              flex: 1;
            }
          `}
        />
        <SocialSharing title={blogPost.title} />
      </Container>
      <BlogSignup />
    </Layout>
  );
};

export default BlogPage;
